/**
 * SD-1207: bank feeds via Stripe Financial Connections.
 *
 * Mirrors the wire shape of api/bankfeeds (BankFeedsController.Project) and
 * Synced.Integration.ViewModels/BankFeeds/TransactionImportRun.cs in the backend repo.
 */

/** Whether an organisation can use bank feeds, and the sentence to show when it cannot. */
export interface BankFeedAvailability {
  available: boolean;
  /** Written for the user: "Bank feeds are only available for US organisations." */
  reason?: string;
  countryCode?: string;
}

/** A Financial Connections session, ready for Stripe.js. */
export interface BankFeedSession {
  sessionId: string;
  /**
   * Handed straight to stripe.collectFinancialConnectionsAccounts. Short-lived and
   * single-purpose, but still a credential: never log it, never put it in a URL.
   */
  clientSecret: string;
}

/**
 * What the UI needs to decide what to show. Deliberately narrower than the raw Stripe
 * status -- a user needs to know whether to act, and "inactive" does not tell them that.
 */
export type BankFeedHealth =
  /** Connected, mapped, subscribed. Nothing to do. */
  | 'ok'
  /** Stripe cannot reach the account. Only a relink fixes it. */
  | 'needs_reconnect'
  /** Linked, but nobody has said which ledger account it belongs to. */
  | 'needs_account'
  /** Mapped, but Stripe's daily refresh is not on -- it will not update by itself. */
  | 'not_subscribed'
  | 'removed';

export interface BankFeedAccount {
  id: string;
  institutionName: string;
  displayName: string;
  last4: string;
  category: string;
  subcategory: string;
  currency: string;
  /** Stripe's own: active | inactive | disconnected. Prefer `health` for display. */
  status: string;
  health: BankFeedHealth;
  /** The ledger account this feed writes into. Null until someone maps it. */
  accountId: string;
  accountName: string;
  isMapped: boolean;
  subscriptionActive: boolean;
  lastImportUtc: string;
  lastRefreshStatus: string;
  nextRefreshAvailableUtc: string;
  /** The earliest date this feed has pulled. Anything older is a CSV upload. */
  historicalImportedFrom: string;
}

/** The outcome of one feed import, as the import endpoints return it. */
export interface BankFeedImportResult {
  requestedCount: number;
  insertedCount: number;
  updatedCount: number;
  duplicateCount: number;
  unmappedCount: number;
  voidedCount: number;
  /**
   * Stripe changes we declined to apply because the row was already reconciled, matched
   * or actioned. Worth surfacing: it means the bank and the books disagree.
   */
  conflictCount: number;
  droppedRows: DroppedTransactionRow[];
  refreshId: string;
  errorMessage: string;
}

/** What an import from a feed would bring in, before anything is written. */
export interface BankFeedPreview {
  /** yyyy-MM-ddTHH:mm:ss; the start date actually used, after clamping. */
  from: string;
  transactions: BankFeedPreviewTransaction[];
  /** Already imported, since changed at the bank: applied on import regardless of selection. */
  updateCount: number;
  /** Already imported, since voided at the bank: withdrawn on import regardless of selection. */
  withdrawCount: number;
}

export interface BankFeedPreviewTransaction {
  /** Stripe fctxn_ id. */
  id: string;
  /** yyyy-MM-dd */
  transactionDate: string;
  description: string;
  amount: number;
  /** pending | posted */
  status: string;
  alreadyImported: boolean;
}

export interface DroppedTransactionRow {
  description: string;
  amount: number;
  transactionDate: string;
  /** "duplicate" | "invalid_account" | "other". */
  reason: string;
}

/**
 * One transaction an import run touched (dbo.TransactionImportRunItem), as it was when the run
 * recorded it, and whether it is still in the ledger and reconciled now.
 */
export interface ImportRunTransaction {
  unreconciledReportId: string | null;
  /** "Inserted" | "Updated" | "Conflict" | "Voided". */
  action: string;
  transactionDate: string | null;
  description: string;
  amount: number | null;
  accountName: string;
  inLedger: boolean;
  isReconciled: boolean;
}

/** A run's transactions (capped by the server) and how many there are in all. */
export interface ImportRunTransactionList {
  transactions: ImportRunTransaction[];
  total: number;
}

/** Where an import's rows came from. Matches ImportSource in the backend. */
export type ImportRunSource =
  | 'StripeFeed' | 'Xero' | 'Spreadsheet' | 'ChromeExtension' | 'API' | 'Unknown';

/** What caused an import. Matches ImportTrigger in the backend. */
export type ImportRunTrigger = 'Manual' | 'Historical' | 'Webhook' | 'Sweep' | 'Scheduled' | 'Refresh';

export type ImportRunStatus = 'Running' | 'Success' | 'Partial' | 'Failed';

/**
 * One recorded import, whatever its source. The Stripe feed, the Xero pull and CSV
 * uploads all appear here.
 */
export interface ImportRun {
  id: string;
  organisationID: string;
  source: ImportRunSource;
  trigger: ImportRunTrigger;
  accountID: string;
  accountName: string;
  bankFeedConnectionID: string;
  reportID: string;
  startedUtc: string;
  finishedUtc: string;
  status: ImportRunStatus;
  requestedCount: number;
  insertedCount: number;
  /** These three only ever move for the Stripe feed: no other source mutates or voids rows. */
  updatedCount: number;
  voidedCount: number;
  conflictCount: number;
  duplicateCount: number;
  unmappedCount: number;
  periodFrom: string;
  periodTo: string;
  /** Stripe's transaction_refresh or event id, so a run traces back to its cause. */
  externalRef: string;
  fileName: string;
  initiatedByUserId: string;
  /** JSON array of DroppedTransactionRow, capped server-side. */
  droppedSampleJson: string;
  errorMessage: string;
}

export interface ImportRunPage {
  runs: ImportRun[];
  total: number;
}

/**
 * What set an import off, in the words a user would use.
 *
 * One function rather than a switch in each screen: Settings > Banking and the Transactions
 * import history both label the same runs, and the same event reading "Daily" in one place
 * and "Automatic" in the other is the drift that makes people stop believing a status
 * display. Webhook and Sweep are both collapsed to "Daily" on purpose -- the distinction
 * between Stripe telling us and us noticing for ourselves is ours to care about, not
 * theirs.
 */
export function importTriggerLabel(run: ImportRun): string {
  switch (run && run.trigger) {
    case 'Manual': return 'Manual';
    case 'Historical': return 'Historical import';
    case 'Webhook': return 'Daily';
    case 'Sweep': return 'Daily (catch-up)';
    case 'Scheduled': return 'Daily sync';
    case 'Refresh': return 'Refresh';
    default: return 'Sync';
  }
}
